import { Heading, IconButton } from '@alto-avios/alto-ui';
import { FC } from 'react';

import { isNonNegativeNumber, pushGTMEvent } from '../../utilities';
import { ErrorCard } from '../ErrorCard';
import { AuthElements, UserDetails } from '../Header';
import { MobileMenuButton } from '../MobileMenuButton';
import { MobileUserDetails } from '../MobileUserDetails';
import { MyAccountDetailsCard } from '../MyAccountDetailsCard';
import { Overlay } from '../Overlay';
import { useOverlayContext } from '../Overlay/useOverlayContext';
import { HeaderContent, useRequiredContent } from '../services/content';
import { useUserDetailsError } from '../../contexts/userDetailsError';

import styles from './MobileUserMenu.module.scss';

export interface MobileUserMenuProps {
  householdAviosBalance?: number;
  loginProps: AuthElements;
  logoutProps: AuthElements;
  userBalance?: number;
  userDetails: UserDetails;
}

const MobileUserMenuContent: FC<MobileUserMenuProps> = ({ householdAviosBalance, loginProps, logoutProps, userBalance, userDetails }) => {
  const overlayContext = useOverlayContext();
  const { userBalanceError } = useUserDetailsError();
  const {
    data: { mobileAccount },
  } = useRequiredContent(HeaderContent);
  const welcomeMessage = mobileAccount.header.replace('{{user.name}}', userDetails.name);

  const onClick = (label: string) => {
    overlayContext?.close();
    pushGTMEvent('manage avios', label);
  };

  return (
    <div className={styles.container}>
      <Heading as="h3" foregroundColor="default" size="xs">
        {welcomeMessage}
      </Heading>
      {(userBalanceError || !isNonNegativeNumber(userBalance)) && <ErrorCard errorMessage={mobileAccount.userDataErrorMessage} />}
      <MyAccountDetailsCard
        householdAviosBalance={householdAviosBalance}
        membershipNumber={userDetails.membershipNumber}
        personalAviosBalance={userBalance}
      />
      <div className={styles.userInformation}>
        <div className={styles.linksWrapper}>
          {mobileAccount.links.map(({ label, leftIcon, url }) => (
            <MobileMenuButton
              aria-label={label}
              hasDivider
              href={url}
              iconStartName={leftIcon}
              key={label}
              onPress={() => onClick(label)}
              size="comfortable"
            >
              {label}
            </MobileMenuButton>
          ))}
        </div>
      </div>
      <div className={styles.flex} />
      <MobileUserDetails isLoggedIn={!!userDetails} loginProps={loginProps} logoutProps={logoutProps} />
    </div>
  );
};

export const MobileUserMenu: FC<MobileUserMenuProps> = ({ householdAviosBalance, loginProps, logoutProps, userBalance, userDetails }) => {
  const { data: header } = useRequiredContent(HeaderContent);

  const trigger = (
    <IconButton
      aria-label={header.mobileAccount.triggerAriaLabel}
      emphasis="quaternary"
      iconProps={{ iconName: 'user', iconPrefix: 'far' }}
      name="user"
      styleVariant="neutral"
    />
  );

  return (
    <Overlay ariaLabel={header.mobileAccount.overlayAriaLabel} trigger={trigger}>
      <MobileUserMenuContent
        householdAviosBalance={householdAviosBalance}
        loginProps={loginProps}
        logoutProps={logoutProps}
        userBalance={userBalance}
        userDetails={userDetails}
      />
    </Overlay>
  );
};
